import React, { useState, useEffect } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import useTheme from '../QuickMtcProposal/hooks/useTheme'
import '../QuickMtcProposal/styles/proposal.css'
import axios from 'axios'

const ENTITY_NAME_MAP = {
  hmi: 'H.M.I. Commercial Landscaping',
  bhl: 'Bee Happy Lawns, LLC',
  dfl: 'Desert Foothills Landscaping, LLC'
}

function getEntityKey (entity) {
  if (!entity || typeof entity !== 'object') return 'hmi'
  const name = (entity.name || entity.internalName || '').toLowerCase().trim()
  return ENTITY_NAME_MAP[name] ? name : 'hmi'
}

const CONTENT_PAD = { padding: '0 40px' }

const SubmittalPreviewPage = () => {
  const { id } = useParams()
  const location = useLocation()
  const isPdfMode = new URLSearchParams(location.search).get('pdf') === '1'
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Use DB template theme (populated by the server from MtcProposalTemplate)
  const theme = useTheme(data?.templateTheme || null)

  const submittal = data?.submittal
  const entityKey = submittal ? getEntityKey(submittal.entity) : 'hmi'
  const entityNameFormal = ENTITY_NAME_MAP[entityKey] || 'H.M.I. Commercial Landscaping'

  useEffect(() => {
    axios.get(`/api/submittals/public/${id}`)
      .then(({ data: d }) => { setData(d); setError(null) })
      .catch(err => setError(err.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false))
  }, [id])

  if (loading) return <div style={{ padding: 40, textAlign: 'center', fontFamily: 'DM Sans, sans-serif' }}>Loading submittal...</div>
  if (error) return <div style={{ padding: 40, textAlign: 'center', color: 'red' }}>{error}</div>
  if (!submittal) return null

  const items = [...(submittal.items || [])].sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
  const fontDisplay = `'${theme.fontDisplay || 'Cormorant Garamond'}', Georgia, serif`
  const fontBody = `'${theme.fontBody || 'DM Sans'}', 'Segoe UI', sans-serif`

  return (
    <div className={isPdfMode ? 'proposal-preview-only' : ''}>
      {/* ===== Cover Page ===== */}
      <div className="proposal-cover" style={{ background: theme.colorPrimary }}>
        <div className="proposal-cover__accent-bar" style={{
          background: `linear-gradient(90deg, ${theme.colorGold}, ${theme.colorAccent})`
        }} />
        <div className="proposal-cover__body">
          <div className="proposal-cover__logo">
            {theme.logoUrl && (
              <img src={theme.logoUrl} alt="Logo" style={{ maxWidth: theme.logoWidth || 180, maxHeight: 80 }} />
            )}
          </div>

          <div className="proposal-cover__subtitle" style={{ fontFamily: fontDisplay }}>
            Material Submittal
          </div>

          <div className="proposal-cover__divider" style={{ backgroundColor: theme.colorGold }} />

          <h1 className="proposal-cover__title" style={{ fontFamily: fontDisplay }}>
            {submittal.name}
          </h1>

          <div className="proposal-cover__client">
            {submittal.generalContractor && (
              <div className="proposal-cover__client-name" style={{ fontFamily: fontDisplay }}>
                {submittal.generalContractor}
              </div>
            )}
            {submittal.architect && (
              <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 15, marginTop: 4, fontFamily: fontBody }}>
                Architect: {submittal.architect}
              </div>
            )}
            <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, marginTop: 12, fontFamily: fontBody }}>
              {today}
            </div>
          </div>

          <div className="proposal-cover__footer" data-entity-name={entityNameFormal}>
            {entityNameFormal}
          </div>
        </div>
      </div>

      {/* ===== Table of Contents ===== */}
      <div className="proposal-page-frame" style={{ pageBreakBefore: 'always' }}>
        <div className="proposal-page-frame__header">
          <div className="proposal-page-frame__header-left">
            {theme.logoUrl && (
              <img className="proposal-page-frame__header-logo" src={theme.logoUrl} alt="" style={{ height: 28 }} />
            )}
            <span className="proposal-page-frame__header-title">Material Submittal</span>
          </div>
        </div>
        {/* Gold bar — full width */}
        <div className="proposal-page-frame__accent-bar" style={{
          background: `linear-gradient(90deg, ${theme.colorGold}, ${theme.colorAccent})`
        }} />
        <div className="proposal-page-frame__content" style={CONTENT_PAD}>
          <h2 style={{ fontFamily: fontDisplay, color: theme.colorPrimary, fontSize: 28, fontWeight: 600, marginBottom: 8 }}>
            Table of Contents
          </h2>
          <div style={{ width: 60, height: 3, backgroundColor: theme.colorGold, marginBottom: 24 }} />

          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: fontBody }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${theme.colorPrimary}` }}>
                {['#', 'Product', 'Manufacturer', 'Category'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 4px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, color: theme.colorMuted }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={item._id} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '6px 4px', fontSize: 13, color: theme.colorMuted }}>{idx + 1}</td>
                  <td style={{ padding: '6px 4px', fontSize: 13, fontWeight: 500 }}>{item.productName}</td>
                  <td style={{ padding: '6px 4px', fontSize: 13, color: theme.colorMuted }}>{item.manufacturer || '—'}</td>
                  <td style={{ padding: '6px 4px', fontSize: 13, color: theme.colorMuted }}>{item.category || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="proposal-page-frame__footer" style={{ backgroundColor: theme.colorPrimary }}>
          <span className="proposal-page-frame__footer-left" data-entity-name={entityNameFormal}>{entityNameFormal}</span>
        </div>
      </div>

      {/* ===== Item Pages ===== */}
      {items.map((item, idx) => (
        <div key={item._id} className="proposal-page-frame" style={{ pageBreakBefore: 'always' }}>
          <div className="proposal-page-frame__header">
            <div className="proposal-page-frame__header-left">
              {theme.logoUrl && (
                <img className="proposal-page-frame__header-logo" src={theme.logoUrl} alt="" style={{ height: 28 }} />
              )}
              <span className="proposal-page-frame__header-title">Material Submittal</span>
            </div>
          </div>
          {/* Gold bar — full width */}
          <div className="proposal-page-frame__accent-bar" style={{
            background: `linear-gradient(90deg, ${theme.colorGold}, ${theme.colorAccent})`
          }} />
          <div className="proposal-page-frame__content" style={CONTENT_PAD}>
            {/* Item Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{
                width: 36, height: 36, borderRadius: '50%',
                backgroundColor: theme.colorPrimary, color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 15, fontWeight: 700, flexShrink: 0
              }}>
                {idx + 1}
              </div>
              <h2 style={{ fontFamily: fontDisplay, color: theme.colorPrimary, fontSize: 26, fontWeight: 600, margin: 0 }}>
                {item.productName}
              </h2>
            </div>
            <div style={{ width: 60, height: 3, backgroundColor: theme.colorGold, marginBottom: 20 }} />

            {/* Spec Grid */}
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr',
              gap: 12, marginBottom: 20, padding: 16,
              backgroundColor: theme.colorBackground || '#f9f9f9', borderRadius: 8,
              fontFamily: fontBody
            }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, color: theme.colorMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Manufacturer</div>
                <div style={{ fontSize: 14, color: theme.colorText }}>{item.manufacturer || '—'}</div>
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, color: theme.colorMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Part Number</div>
                <div style={{ fontSize: 14, color: theme.colorText }}>{item.manufacturerPartNumber || item.vendorItemNumber || '—'}</div>
              </div>
              {item.vendorItemNumber && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: theme.colorMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>SKU / Item #</div>
                  <div style={{ fontSize: 14, color: theme.colorText }}>{item.vendorItemNumber}</div>
                </div>
              )}
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, color: theme.colorMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Category</div>
                <div style={{ fontSize: 14, color: theme.colorText }}>{item.category || '—'}</div>
              </div>
              {item.quantity > 0 && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 600, color: theme.colorMuted, textTransform: 'uppercase', letterSpacing: 0.5 }}>Quantity</div>
                  <div style={{ fontSize: 14, color: theme.colorText }}>{item.quantity} {item.unit}</div>
                </div>
              )}
            </div>

            {/* Description */}
            {item.shortDescription && (
              <p style={{ fontSize: 13, lineHeight: 1.6, color: theme.colorText, marginBottom: 12, fontFamily: fontBody }}>
                {item.shortDescription}
              </p>
            )}

            {item.rawSpecText && (
              <p style={{ fontSize: 12, lineHeight: 1.5, color: theme.colorMuted, whiteSpace: 'pre-wrap', marginBottom: 12, fontFamily: fontBody }}>
                {item.rawSpecText}
              </p>
            )}

            {/* Notes */}
            {(item.notes || item.specOverrides) && (
              <div style={{
                padding: 12, backgroundColor: '#fff9c4', borderRadius: 4,
                borderLeft: `4px solid ${theme.colorGold}`, marginBottom: 16,
                fontSize: 13, fontFamily: fontBody
              }}>
                {item.specOverrides && <div><strong>Spec Notes:</strong> {item.specOverrides}</div>}
                {item.notes && <div><strong>Notes:</strong> {item.notes}</div>}
              </div>
            )}

            {/* Product Image (API-sourced) or Cutsheet Image (PDF-sourced) */}
            {item.productImageUrl ? (
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <img
                  src={item.productImageUrl}
                  alt={item.productName}
                  style={{ maxWidth: '100%', maxHeight: 500, objectFit: 'contain', border: '1px solid #ddd', borderRadius: 4 }}
                />
              </div>
            ) : item.vendorCatalogId && item.pageNumber ? (
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <img
                  src={`/api/vendor-catalogs/${item.vendorCatalogId}/page-image/${item.pageNumber}`}
                  alt={`Cutsheet - ${item.productName}`}
                  style={{ maxWidth: '100%', maxHeight: 500, border: '1px solid #ddd', borderRadius: 4 }}
                />
              </div>
            ) : null}

            {/* Structured Specifications (API-sourced products) */}
            {item.productSpecifications && item.productSpecifications.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12, fontFamily: fontBody }}>
                <tbody>
                  {item.productSpecifications.map((spec, si) => (
                    <tr key={si} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '5px 8px', fontWeight: 500, fontSize: 12, width: '40%', color: theme.colorMuted }}>{spec.label}</td>
                      <td style={{ padding: '5px 8px', fontSize: 12, color: theme.colorText }}>{spec.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Document Links */}
            {item.documentUrls && item.documentUrls.length > 0 && (
              <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
                {item.documentUrls.map((doc, i) => (
                  <a
                    key={i}
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 16px', border: '1px solid #ddd', borderRadius: 8,
                      textDecoration: 'none', color: theme.colorText, fontFamily: fontBody
                    }}
                  >
                    <span style={{ fontSize: 24, color: doc.label === 'SDS' ? '#d32f2f' : theme.colorPrimary }}>&#128196;</span>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{doc.label || 'Document'}</div>
                      <div style={{ fontSize: 11, color: theme.colorMuted }}>View PDF</div>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>
          <div className="proposal-page-frame__footer" style={{ backgroundColor: theme.colorPrimary }}>
            <span className="proposal-page-frame__footer-left">{entityNameFormal}</span>
          </div>
        </div>
      ))}
    </div>
  )
}

export default SubmittalPreviewPage
